import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Apple } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";
import { toast } from "@/lib/toast";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline } from "@/components/primitives";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getAuthRedirectUrl, getOAuthRedirectUrl } from "@/lib/authRedirect";
import { signInWithApple } from "@/lib/appleSignIn";

/**
 * Waitlist-gated signup page.
 *
 * Flow:
 *   1. User arrives from /waitlist's "Continue to sign up" link.
 *   2. They enter email + password OR pick Apple/Google.
 *   3. Before creating the account (email/pw) or starting OAuth (Apple/Google),
 *      we call the `check_waitlist_approved` RPC. If the email isn't approved,
 *      block and explain.
 *   4. On success, AuthCallback runs the same check for OAuth providers and
 *      hands off to /onboard (where step 2 detects existing session and skips
 *      the account-creation step).
 */
export default function Signup() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const prefilledEmail = (search.get("email") ?? "").trim().toLowerCase();
  const { session } = useAuth();
  const [email, setEmail] = useState(prefilledEmail);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const initialSessionUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (initialSessionUserId.current === undefined) {
      initialSessionUserId.current = session?.user?.id ?? null;
      return;
    }
    const currentId = session?.user?.id ?? null;
    if (currentId && currentId !== initialSessionUserId.current) {
      nav("/", { replace: true });
    }
  }, [session, nav]);

  /**
   * Returns true if the given email is on the approved waitlist.
   * Shows a toast on rejection.
   */
  const ensureApproved = async (rawEmail: string): Promise<boolean> => {
    const trimmed = rawEmail.trim().toLowerCase();
    if (!trimmed) {
      toast.error("Enter an email first.");
      return false;
    }
    const { data, error } = await supabase.rpc("check_waitlist_approved", { check_email: trimmed });
    if (error) {
      toast.error(error.message ?? "Couldn't check waitlist status");
      return false;
    }
    if (!data) {
      toast.error("This email isn't approved yet. Join the waitlist first.");
      return false;
    }
    return true;
  };

  const handleEmailSignup = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ok = await ensureApproved(email);
      if (!ok) return;
      // Sends Supabase's "Confirm signup" verification email (NOT a
      // magic link). The password we pass here is a throwaway —
      // UsernameStep overwrites it with the user's real password via
      // updateUser() after they verify and complete onboarding. We use
      // crypto.randomUUID() for a strong, unguessable temp value.
      const tempPassword = crypto.randomUUID() + crypto.randomUUID();
      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: tempPassword,
        options: { emailRedirectTo: getAuthRedirectUrl("/auth/callback") },
      });
      if (error) {
        if (/already registered|already exists/i.test(error.message)) {
          toast.error("That email is already registered. Try signing in.");
        } else {
          toast.error(error.message ?? "Couldn't send the verification email.");
        }
        return;
      }
      setPendingEmail(email.trim().toLowerCase());
    } finally {
      setBusy(false);
    }
  };

  const handleApple = async () => {
    setBusy(true);
    try {
      // Native Sign in with Apple on iOS, web OAuth elsewhere. Always
      // reset busy in finally so the email/password submit doesn't lock.
      await signInWithApple();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Apple sign-up failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: getOAuthRedirectUrl("/auth/callback?from=signup") },
    });
    if (error) {
      toast.error(error.message ?? "Google sign-up failed");
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setBusy(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: pendingEmail,
      options: { emailRedirectTo: getAuthRedirectUrl("/auth/callback") },
    });
    setBusy(false);
    if (error) toast.error(error.message);
    else toast.success("Verification email resent.");
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid hsl(var(--ink))",
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 13,
    color: "hsl(var(--ink))",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "hsl(var(--ink-2))",
    marginBottom: 6,
  };

  // Pending verification state
  if (pendingEmail) {
    return (
      <MobileFrame>
        <div
          className="paper-bg"
          style={{
            minHeight: "100vh",
            padding: "60px 28px 40px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center" }}>
            <h1
              style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 700,
                fontSize: 32,
                color: "hsl(var(--ink))",
              }}
            >
              Check your inbox.
            </h1>
            <p
              style={{
                maxWidth: 320,
                fontFamily: "'Fraunces', serif",
                fontSize: 15,
                lineHeight: 1.6,
                color: "hsl(var(--ink-2))",
              }}
            >
              We sent a verification link to{" "}
              <strong style={{ color: "hsl(var(--ink))" }}>{pendingEmail}</strong>. Click it to finish setting up your account.
            </p>
            <button
              type="button"
              onClick={handleResend}
              disabled={busy}
              style={{
                marginTop: 8,
                background: "none",
                border: "none",
                cursor: busy ? "wait" : "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "hsl(var(--ink-2))",
              }}
            >
              {busy ? "…" : "Resend verification email"}
            </button>
          </div>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <div
        className="paper-bg"
        style={{
          minHeight: "100vh",
          padding: "60px 28px 40px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 900,
              fontStyle: "italic",
              fontSize: 80,
              letterSpacing: "-0.06em",
              lineHeight: 0.85,
              color: "hsl(var(--ink))",
            }}
          >
            fig
          </div>
          <p
            className="caps"
            style={{ color: "hsl(var(--rust))", marginTop: 6 }}
          >
            Sign up
          </p>
        </div>

        <Hairline />

        {/* Form */}
        <form onSubmit={handleEmailSignup} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 20 }}>
          <div>
            <label htmlFor="signup-email" style={labelStyle}>
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              required
              autoComplete="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>

          <p style={{ fontSize: 12, color: "hsl(var(--ink-2))", opacity: 0.7, marginBottom: 4, textAlign: "center" }}>
            We'll send a verification email. Click the link inside to confirm your email and finish signing up — you'll pick a username and password on the next screen. By continuing you agree to our <a href="/terms" style={{ color: "hsl(var(--ink))" }}>Terms</a> and <a href="/privacy" style={{ color: "hsl(var(--ink))" }}>Privacy Policy</a>.
          </p>

          <button
            type="submit"
            disabled={busy}
            style={{
              width: "100%",
              padding: "14px 18px",
              background: "hsl(var(--ink))",
              color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "…" : "Send verification email →"}
          </button>
        </form>

        {/* OAuth divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 14px" }}>
          <div style={{ flex: 1, height: 1, background: "hsl(var(--ink))", opacity: 0.2 }} />
          <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "hsl(var(--ink))", opacity: 0.2 }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <button
            type="button"
            onClick={handleApple}
            disabled={busy}
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1px solid hsl(var(--ink))",
              background: "#000",
              color: "#fff",
              borderRadius: 8,
              cursor: busy ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              fontWeight: 600,
              opacity: busy ? 0.7 : 1,
            }}
          >
            <Apple size={18} />
            Continue with Apple
          </button>
          <button
            type="button"
            onClick={handleGoogle}
            disabled={busy}
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1px solid hsl(var(--ink))",
              background: "#fff",
              color: "#1f1f1f",
              borderRadius: 8,
              cursor: busy ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              fontWeight: 600,
              opacity: busy ? 0.7 : 1,
            }}
          >
            <GoogleIcon size={18} />
            Continue with Google
          </button>
        </div>

        <div style={{ marginTop: 24, textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            type="button"
            onClick={() => nav("/auth")}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "hsl(var(--ink-2))",
            }}
          >
            Already have an account? Log in →
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}
